/**
 * V5 segment-based interpolation.
 *
 * Instead of linearly interpolating between two lat/lon points,
 * this interpolates along a segment geometry using time_intervals
 * from the V5 service, following the actual route path.
 */

import type { SegmentState, TimeInterval } from '../stores/segmentStore';
import { easeOutCubic } from './interpolation';

/** Duration of the ease-out blend when segment data updates (ms) */
const TRANSITION_DURATION_MS = 2500;

/**
 * Pure helper: compute a position from time_intervals and segment geometry
 * using wall-clock time.
 *
 * @param timeIntervals - 4-entry geOps-style time_intervals
 * @param segmentGeometry - Polyline as [[lat, lon], ...]
 * @param wallNow - Wall-clock timestamp in ms (Date.now())
 * @returns [latitude, longitude] or null if data is insufficient
 */
export function interpolateFromTimeIntervals(
  timeIntervals: TimeInterval[],
  segmentGeometry: [number, number][],
  wallNow: number
): [number, number] | null {
  if (timeIntervals.length < 4 || segmentGeometry.length < 2) {
    return null;
  }

  const fraction = computeFractionFromTimeIntervals(timeIntervals, wallNow);
  return interpolateAlongGeometry(segmentGeometry, fraction);
}

/**
 * Interpolate a vehicle's position along its current segment geometry.
 *
 * Uses the 4-entry time_intervals to determine where along the segment
 * the vehicle should be at the current time, then maps that fraction
 * to a coordinate on the segment geometry.
 *
 * When a segment update arrives, blends smoothly from the previous visual
 * position (frozenBlendOrigin) to the new computed position using an
 * ease-out cubic curve over TRANSITION_DURATION_MS.
 *
 * time_intervals format (geOps-style):
 *   [0] = [arrival_at_from_stop_ms, 0.0, heading]   // dwell start
 *   [1] = [departure_from_from_stop_ms, 0.0, heading]  // dwell end
 *   [2] = [arrival_at_to_stop_ms, 1.0, heading]     // drive end
 *   [3] = [departure_from_to_stop_ms, 1.0, heading]    // next dwell end
 *
 * @param segment - The current segment state
 * @param animNow - Animation timestamp from performance.now() (for blend elapsed)
 * @returns [latitude, longitude] or null if segment data is insufficient
 */
export function interpolateSegmentPosition(
  segment: SegmentState,
  animNow: number = performance.now()
): [number, number] | null {
  const wallNow = Date.now();

  const newPos = interpolateFromTimeIntervals(
    segment.time_intervals,
    segment.segment_geometry,
    wallNow
  );

  if (!newPos) return null;

  // If a blend transition is in progress, ease from frozen origin to new position
  if (segment.frozenBlendOrigin && segment.transitionStartTime !== null) {
    const elapsed = animNow - segment.transitionStartTime;

    if (elapsed >= TRANSITION_DURATION_MS) {
      return newPos;
    }

    const rawT = Math.max(0, elapsed / TRANSITION_DURATION_MS);
    const alpha = easeOutCubic(rawT);

    return [
      segment.frozenBlendOrigin[0] + alpha * (newPos[0] - segment.frozenBlendOrigin[0]),
      segment.frozenBlendOrigin[1] + alpha * (newPos[1] - segment.frozenBlendOrigin[1]),
    ];
  }

  return newPos;
}

/**
 * Compute the current fraction (0.0 to 1.0) along a segment
 * based on the time_intervals and current time.
 */
export function computeFractionFromTimeIntervals(
  intervals: TimeInterval[],
  now: number
): number {
  // Extract timestamps
  const arrivalAtFrom = intervals[0][0];  // Arrival at from_stop
  const departureFromFrom = intervals[1][0];  // Departure from from_stop
  const arrivalAtTo = intervals[2][0];  // Arrival at to_stop
  // intervals[3][0] = departure from to_stop (unused, kept for documentation)

  // Before arrival at from_stop: at start
  if (now <= arrivalAtFrom) {
    return 0.0;
  }

  // During dwell at from_stop (arrival → departure): stay at 0.0
  if (now <= departureFromFrom) {
    return 0.0;
  }

  // Driving between stops (departure from from_stop → arrival at to_stop)
  if (now <= arrivalAtTo) {
    const driveTime = arrivalAtTo - departureFromFrom;
    if (driveTime <= 0) return 1.0;
    const elapsed = now - departureFromFrom;
    return Math.min(1.0, elapsed / driveTime);
  }

  // At or past to_stop: fraction = 1.0
  return 1.0;
}

/**
 * Interpolate a point along a polyline geometry given a fraction (0.0-1.0).
 *
 * Walks the polyline segments, accumulating distance, and interpolates
 * the position at the given fraction of total length.
 */
export function interpolateAlongGeometry(
  geometry: [number, number][],
  fraction: number
): [number, number] {
  if (geometry.length === 0) return [0, 0];
  if (geometry.length === 1) return geometry[0];
  if (fraction <= 0) return geometry[0];
  if (fraction >= 1) return geometry[geometry.length - 1];

  // Compute cumulative distances
  const distances: number[] = [0];
  let totalDistance = 0;

  for (let i = 1; i < geometry.length; i++) {
    const dx = geometry[i][0] - geometry[i - 1][0];
    const dy = geometry[i][1] - geometry[i - 1][1];
    totalDistance += Math.sqrt(dx * dx + dy * dy);
    distances.push(totalDistance);
  }

  if (totalDistance === 0) return geometry[0];

  // Find the target distance along the polyline
  const targetDist = fraction * totalDistance;

  // Find which segment of the polyline contains this distance
  for (let i = 1; i < distances.length; i++) {
    if (targetDist <= distances[i]) {
      const segmentLength = distances[i] - distances[i - 1];
      if (segmentLength === 0) return geometry[i];
      const segFraction = (targetDist - distances[i - 1]) / segmentLength;
      return [
        geometry[i - 1][0] + (geometry[i][0] - geometry[i - 1][0]) * segFraction,
        geometry[i - 1][1] + (geometry[i][1] - geometry[i - 1][1]) * segFraction,
      ];
    }
  }

  return geometry[geometry.length - 1];
}

/**
 * Get the heading from the current time_intervals entry.
 * Returns degrees (converted from radians) or null.
 */
export function getSegmentHeading(
  intervals: TimeInterval[],
  now: number = Date.now()
): number | null {
  if (intervals.length < 4) return null;

  // Use the heading from the most relevant interval
  // During driving, use the departure heading; at stop, use arrival heading
  const departureFromFrom = intervals[1][0];
  const arrivalAtTo = intervals[2][0];

  let headingRad: number | null;
  if (now <= departureFromFrom) {
    headingRad = intervals[0][2];
  } else if (now <= arrivalAtTo) {
    headingRad = intervals[1][2];
  } else {
    headingRad = intervals[2][2];
  }

  if (headingRad === null || headingRad === undefined) return null;
  return (headingRad * 180) / Math.PI;
}
